// Utility to create a canvas with an alpha mask that has a feather (soft) edge.
// Returns a canvas where the image has been masked with a blurred alpha mask.
export async function createFeatheredCanvas(
  image: HTMLImageElement | HTMLCanvasElement,
  dstWidth: number,
  dstHeight: number,
  featherPx: number,
  shape: 'rect' | 'ellipse' | 'round' = 'rect',
  cornerRadius: number = 0
): Promise<HTMLCanvasElement> {
  // Ensure integer sizes
  const w = Math.max(1, Math.round(dstWidth));
  const h = Math.max(1, Math.round(dstHeight));

  // Mask (raw) canvas: white where image should be opaque
  const maskRaw = document.createElement('canvas');
  maskRaw.width = w;
  maskRaw.height = h;
  const mctx = maskRaw.getContext('2d')!;

  mctx.fillStyle = 'black';
  mctx.fillRect(0, 0, w, h);
  mctx.fillStyle = 'white';

  if (shape === 'ellipse') {
    mctx.beginPath();
    mctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    mctx.closePath();
    mctx.fill();
  } else if (shape === 'round' && cornerRadius > 0) {
    const r = cornerRadius;
    mctx.beginPath();
    mctx.moveTo(r, 0);
    mctx.lineTo(w - r, 0);
    mctx.quadraticCurveTo(w, 0, w, r);
    mctx.lineTo(w, h - r);
    mctx.quadraticCurveTo(w, h, w - r, h);
    mctx.lineTo(r, h);
    mctx.quadraticCurveTo(0, h, 0, h - r);
    mctx.lineTo(0, r);
    mctx.quadraticCurveTo(0, 0, r, 0);
    mctx.closePath();
    mctx.fill();
  } else {
    mctx.fillRect(0, 0, w, h);
  }

  // Blur the mask into maskBlur
  const maskBlur = document.createElement('canvas');
  maskBlur.width = w;
  maskBlur.height = h;
  const bctx = maskBlur.getContext('2d')!;

  // Prefer canvas filter blur where available
  try {
    if ('filter' in (bctx as any)) {
      (bctx as any).filter = `blur(${featherPx}px)`;
      bctx.drawImage(maskRaw, 0, 0);
      (bctx as any).filter = 'none';
    } else {
      // Fallback: cheap scaled blur approximation
      const k = Math.max(1, Math.ceil(Math.max(1, featherPx) / 4));
      const smallW = Math.max(1, Math.floor(w / k));
      const smallH = Math.max(1, Math.floor(h / k));
      const tmp = document.createElement('canvas');
      tmp.width = smallW;
      tmp.height = smallH;
      const tctx = tmp.getContext('2d')!;
      // Downscale
      tctx.drawImage(maskRaw, 0, 0, smallW, smallH);
      // Upscale with smoothing to produce blur-like result
      bctx.imageSmoothingEnabled = true;
      bctx.imageSmoothingQuality = 'high';
      bctx.drawImage(tmp, 0, 0, w, h);
    }
  } catch (err) {
    // If anything goes wrong, fallback to direct copy (no blur)
    bctx.drawImage(maskRaw, 0, 0);
  }

  // Final canvas: draw the image then apply the blurred mask using destination-in
  const final = document.createElement('canvas');
  final.width = w;
  final.height = h;
  const fctx = final.getContext('2d')!;

  fctx.clearRect(0, 0, w, h);
  // Draw image scaled to destination rectangle
  fctx.drawImage(image as CanvasImageSource, 0, 0, w, h);

  // Apply the mask as alpha
  fctx.save();
  fctx.globalCompositeOperation = 'destination-in';
  fctx.drawImage(maskBlur, 0, 0);
  fctx.restore();

  return final;
}

// Auto-crop settings interface for type safety
export interface AutoCropSettings {
  enabled: boolean;
  paddingTop: number;      // Percentage (0-50)
  paddingBottom: number;   // Percentage (0-50)
  paddingLeft: number;     // Percentage (0-50)
  paddingRight: number;    // Percentage (0-50)
  featherPercent: number;  // Percentage (0-100)
  cropShape: 'rect' | 'ellipse';
}

// Apply auto-crop settings to create a cropped and feathered image
// Returns a data URL of the cropped image
export async function applyAutoCrop(
  imageUrl: string,
  settings: AutoCropSettings
): Promise<{ dataUrl: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = async () => {
      try {
        const originalWidth = img.width;
        const originalHeight = img.height;
        
        // Calculate crop area based on padding percentages
        const cropLeft = Math.round(originalWidth * (settings.paddingLeft / 100));
        const cropRight = Math.round(originalWidth * (settings.paddingRight / 100));
        const cropTop = Math.round(originalHeight * (settings.paddingTop / 100));
        const cropBottom = Math.round(originalHeight * (settings.paddingBottom / 100));
        
        // Calculate cropped dimensions
        const croppedWidth = Math.max(1, originalWidth - cropLeft - cropRight);
        const croppedHeight = Math.max(1, originalHeight - cropTop - cropBottom);
        
        // Create canvas for cropped image
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = croppedWidth;
        cropCanvas.height = croppedHeight;
        const cropCtx = cropCanvas.getContext('2d')!;
        
        // Draw the cropped portion of the image
        cropCtx.drawImage(
          img,
          cropLeft, cropTop, croppedWidth, croppedHeight,  // Source rectangle
          0, 0, croppedWidth, croppedHeight                 // Destination rectangle
        );
        
        // Calculate feather pixels based on percentage
        const minDimension = Math.min(croppedWidth, croppedHeight);
        const featherPx = Math.round(minDimension * (settings.featherPercent / 100) / 2);
        
        // Apply feather effect using the existing createFeatheredCanvas function
        const featheredCanvas = await createFeatheredCanvas(
          cropCanvas,
          croppedWidth,
          croppedHeight,
          featherPx,
          settings.cropShape,
          0
        );
        
        // Convert to data URL
        const dataUrl = featheredCanvas.toDataURL('image/png');
        
        resolve({
          dataUrl,
          width: croppedWidth,
          height: croppedHeight
        });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for auto-crop'));
    };
    
    img.src = imageUrl;
  });
}
